import { LANGUES_DU_MONDE, LANGUES_PARLEES, type LangueParlee } from '@sirocco/shared';
import { locale, t } from '../i18n';

/**
 * Des langues telles que la base les range : `{ fr: true, en: true }`.
 *
 * Une carte plutot qu'un tableau : la Realtime Database range un tableau
 * sous des cles numeriques, et retirer une langue du milieu y laisserait
 * un trou. Une cle par langue ne peut ni se dedoubler ni se decaler.
 */
export type CarteDeLangues = Partial<Record<LangueParlee, true>>;

/**
 * Trois langues au plus pour un evenement.
 *
 * Au-dela, la liste ne dit plus ce qu'on parlera sur place : elle dit
 * qu'on parlera de tout, donc de rien en particulier.
 */
export const LANGUES_MAX_PAR_SORTIE = 3;

export function versCarte(langues: readonly LangueParlee[]): CarteDeLangues {
  return Object.fromEntries(langues.map((code) => [code, true])) as CarteDeLangues;
}

/** Dans l'ordre de l'application, sans ce qui ne serait pas une langue connue. */
export function versListe(carte: CarteDeLangues | null | undefined): LangueParlee[] {
  if (!carte) return [];
  return LANGUES_PARLEES.filter((code) => carte[code] === true);
}

/** Une langue en commun suffit pour se comprendre. */
export function seComprennent(
  a: readonly LangueParlee[],
  b: readonly LangueParlee[],
): boolean {
  return a.some((code) => b.includes(code));
}

/**
 * Les sorties dans mes langues d'abord, les autres ensuite.
 *
 * Un tri stable : a l'interieur de chaque groupe, l'ordre de la liste
 * reste celui des dates. Les autres ne disparaissent pas — un fil un peu
 * multilingue se supporte, et l'on peut parler plus qu'on ne l'a dit.
 */
export function mesLanguesDAbord<T extends { langues: readonly LangueParlee[] }>(
  sorties: readonly T[],
  miennes: readonly LangueParlee[],
): T[] {
  if (miennes.length === 0) return [...sorties];
  const dedans = sorties.filter((sortie) => seComprennent(sortie.langues, miennes));
  const dehors = sorties.filter((sortie) => !seComprennent(sortie.langues, miennes));
  return [...dedans, ...dehors];
}

/** Le nom d'une langue dans la sienne, et son drapeau. */
/**
 * Ce qui est coche d'abord, puis le reste, chaque groupe gardant l'ordre
 * recu — alphabetique la ou on l'appelle.
 */
export function cocheesDAbord<T>(tous: readonly T[], estCochee: (item: T) => boolean): T[] {
  return [...tous.filter(estCochee), ...tous.filter((item) => !estCochee(item))];
}

export function ficheDe(code: LangueParlee): { nom: string; drapeau: string } {
  return LANGUES_DU_MONDE.find((langue) => langue.code === code) ?? { nom: code, drapeau: '' };
}

/** « Français, English » : chaque langue ecrite dans la sienne. */
export function nomsDe(langues: readonly LangueParlee[]): string {
  return langues.map((code) => ficheDe(code).nom).join(', ');
}

/** Les drapeaux cote a cote, pour se poser pres d'un titre. */
export function drapeauxDe(langues: readonly LangueParlee[]): string {
  return langues.map((code) => ficheDe(code).drapeau).join('');
}

let noms: Intl.DisplayNames | null | undefined;

/**
 * Le nom d'une langue dans celle de l'application : « allemand » pour
 * qui lit en francais. C'est ce qu'on tape pour la chercher.
 *
 * Le navigateur le connait, sauf pour les langues du Maroc que ses
 * tables ignorent : celles-la ont leur nom dans nos dictionnaires. Un
 * systeme trop ancien rend `null`, et la liste s'en tient aux noms
 * d'origine.
 */
export function nomLocal(code: LangueParlee): string | null {
  if (code === 'ary' || code === 'zgh') return t.sorties.creation.languesNoms[code];
  if (noms === undefined) {
    try {
      noms = new Intl.DisplayNames([locale], { type: 'language' });
    } catch {
      noms = null;
    }
  }
  try {
    const nom = noms?.of(code);
    if (!nom || nom === code) return null;
    return nom.charAt(0).toLocaleUpperCase(locale) + nom.slice(1);
  } catch {
    return null;
  }
}

/** Sans accents ni majuscules : « ESPANOL » trouve « Español ». */
export function sansAccents(texte: string): string {
  return texte.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}
